import React, { useMemo, useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./styles/Services.css";
import heroBg from "../../assets/images/services/Support& Training-Hero.png";

const SupportTraining = () => {
  const navigate = useNavigate();
  const ctaRef = useRef(null);
  const [ctaInView, setCtaInView] = useState(false);

  useEffect(() => {
    const el = ctaRef.current;
    if (!el) return;

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setCtaInView(true);
          obs.unobserve(entry.target); // animate once
        }
      },
      { threshold: 0.25 }
    );

    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const tracks = useMemo(
    () => ({
      "Network Operations": {
        title: "Network Operations Training",
        points: [
          "Day-to-day monitoring, troubleshooting, and escalation flow.",
          "Switching/Wi‑Fi basics, backups, and safe change practices.",
          "Incident handling checklist and documentation habits.",
        ],
      },
      "Security Basics": {
        title: "Security Fundamentals Training",
        points: [
          "Least privilege, MFA, password policy, and secure admin routines.",
          "Firewall policy basics and safe rule change approach.",
          "Awareness: phishing patterns, endpoint hygiene, and reporting.",
        ],
      },
      "Deployment Readiness": {
        title: "Deployment Readiness Workshop",
        points: [
          "Cutover planning, rollback thinking, and validation steps.",
          "Change windows, stakeholder communication, and approvals.",
          "Post go‑live monitoring and stabilization routine.",
        ],
      },
    }),
    []
  );

  const trackKeys = Object.keys(tracks);
  const [activeTrack, setActiveTrack] = useState(trackKeys[0]);

  return (
    <div className="servicePage">
      {/* HERO — shared markup/classnames used by every service detail page */}
      <section className="hero-section">
        <div className="hero-section__grid-bg" aria-hidden="true" />

        <div className="hero-left">
          <div className="dd-serviceLabel">
            <span className="live-dot" aria-hidden="true" />
            <span>SUPPORT AND TRAINING</span>
          </div>

          <h1 className="hc-hero__title">Empower Success with Expert Support and Training</h1>

          <p className="hc-hero__lead">
            Keep your team running smoothly and confidently with ongoing technical support and focused training
            programs tailored for operational excellence.
          </p>

          <button className="btn-orange" type="button" onClick={() => navigate("/contact")}>
            <span className="btn-text">Meet Our Experts Today</span>
            <span className="btn-arrow" aria-hidden="true">
              ›
            </span>
          </button>
        </div>

        <div className="hero-right">
          <div className="hero-image-container">
            <img className="hero-img" src={heroBg} alt="Support and training" />
            <span className="scan-corner tl" aria-hidden="true" />
            <span className="scan-corner tr" aria-hidden="true" />
            <span className="scan-corner bl" aria-hidden="true" />
            <span className="scan-corner br" aria-hidden="true" />
          </div>
        </div>
      </section>

      {/* How we help you choose */}
      <section className="st-flow">
        <div className="st-flow__container">
          <div className="st-flow__header">
            <span className="section-eyebrow">
              <span className="live-dot" aria-hidden="true" />
              Process
            </span>
            <h2 className="st-h2">How we help you choose</h2>
            <p className="st-muted">
              A structured workflow: define requirements, shortlist vendors, validate claims with a site visit, then
              finalize SLA and onboarding.
            </p>
          </div>

          <div className="st-flow__grid">
            {[
              { t: "1) Discovery", d: "Understand workloads, growth, power density, connectivity and compliance needs." },
              { t: "2) Shortlist", d: "Filter providers by location, budget, carrier options, and support capability." },
              { t: "3) Site visit", d: "Verify physical security, facility upkeep, and operational readiness on-site." },
              { t: "4) Score & select", d: "Use a consistent scorecard to compare vendors fairly across key criteria." },
              { t: "5) Onboarding plan", d: "Finalize cross-connects, access process, and cutover/migration schedule." },
            ].map((s) => (
              <div key={s.t} className="st-flowCard">
                <h3 className="st-h3">{s.t}</h3>
                <p className="st-muted">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Training tracks (tabs) */}
      <section className="st-tracks">
        <div className="st-tracks__container">
          <div className="st-tracks__header">
            <span className="section-eyebrow">
              <span className="live-dot" aria-hidden="true" />
              Training
            </span>
            <h2 className="st-h2">Training tracks</h2>
            <p className="st-muted">
              Choose a track based on your team's role. Sessions can be delivered as workshops and then reinforced
              with practical runbooks and checklists.
            </p>
          </div>

          <div className="st-tabs" role="tablist" aria-label="Training tracks">
            {trackKeys.map((k) => (
              <button
                key={k}
                type="button"
                className={`st-tab ${activeTrack === k ? "active" : ""}`}
                onClick={() => setActiveTrack(k)}
                role="tab"
                aria-selected={activeTrack === k}
              >
                {k}
              </button>
            ))}
          </div>

          <div className="st-trackCard tab-enter" key={activeTrack} role="tabpanel">
            <h3 className="st-h3">{tracks[activeTrack].title}</h3>
            <ul className="st-list">
              {tracks[activeTrack].points.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>

            <button className="st-outlineBtn" type="button" onClick={() => navigate("/contact")}>
              Request Training
            </button>
          </div>
        </div>
      </section>

      {/* Support model (SLA tiles) */}
      <section className="st-support">
        <div className="st-support__container">
          <div className="st-support__left">
            <span className="section-eyebrow">
              <span className="live-dot" aria-hidden="true" />
              Support
            </span>
            <h2 className="st-h2">Support model</h2>
            <p className="st-muted">
              Structured support helps teams resolve issues faster, reduce downtime, and keep changes controlled with
              clear escalation and documentation.
            </p>
            <button className="btn-orange" type="button" onClick={() => navigate("/contact")}>
              <span className="btn-text">Talk to Support</span>
              <span className="btn-arrow" aria-hidden="true">
                ›
              </span>
            </button>
          </div>

          <div className="st-support__right">
            {[
              { t: "Remote support", d: "Guided troubleshooting, configuration checks, and change assistance." },
              { t: "Escalation path", d: "Clear priority handling and handoff rules for faster resolution." },
              { t: "Maintenance support", d: "Planned changes, patching guidance, and safe rollbacks." },
              { t: "Documentation updates", d: "Keep diagrams, runbooks, and inventories aligned to reality." },
            ].map((x) => (
              <div key={x.t} className="st-tile">
                <h3 className="st-tile__title">{x.t}</h3>
                <p className="st-tile__text">{x.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA BANNER — reuses the shared IT-cta-* rules already in Services.css */}
      <section className="IT-cta-section">
        <div ref={ctaRef} className={`IT-cta-card IT-cta-reveal ${ctaInView ? "in" : ""}`}>
          <div className="IT-cta-left">
            <img
              className="IT-cta-illustration"
              src="https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=1400&q=80"
              alt="Support illustration"
            />
          </div>

          <div className="IT-cta-right">
            <h2>
              Support When You Need It. <span>Training That Sticks.</span>
            </h2>

            <p>
              Solve issues quickly and upskill your team with structured training and ongoing support designed to
              reduce downtime and improve day‑to‑day confidence.
            </p>

            <button className="IT-cta-btn" type="button" onClick={() => navigate("/contact")}>
              Find My Support & Training Plan
            </button>
          </div>
        </div>
      </section>

      {/* Migration readiness */}
      <section className="st-move">
        <div className="st-move__container">
          <span className="section-eyebrow">
            <span className="live-dot" aria-hidden="true" />
            Readiness
          </span>
          <h2 className="st-h2">Migration readiness</h2>
          <p className="st-muted">
            Once the facility is selected, prepare a clean onboarding plan to reduce risk and downtime during the
            move.
          </p>

          <div className="st-move__grid">
            <div className="st-moveCard">
              <h3 className="st-h3">Inventory & labeling</h3>
              <p className="st-muted">Hardware list, ports, cabling map, and ownership tags for faster rack/stack.</p>
            </div>
            <div className="st-moveCard">
              <h3 className="st-h3">Connectivity plan</h3>
              <p className="st-muted">Cross-connect requests, ISP handoff, IP plan, and test/validation steps.</p>
            </div>
            <div className="st-moveCard">
              <h3 className="st-h3">Cutover checklist</h3>
              <p className="st-muted">Rollback plan, maintenance window, monitoring, and post-move stabilization.</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SupportTraining;